import React from "react";
import type { SingleValue } from "react-select";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../../../api/useCategories";
import t from "../../../../../i18n";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { ApiValue } from "../../Forms/formHelpers";
import { getDefaultValue, multipleValue } from "../../Forms/formHelpers";
import { uniq } from "diagnostics-lib/array";
import type { ClassificationQuantity } from "../../../../../api/useClassificationQuantities";

// EquipmentType (unique with full option)
export const useEquipmentTypeInput = (selectQuantities: ClassificationQuantity[]) => {
  const [placeholder, setPlaceholder] = React.useState(
    t("setup.paragraphs.quantities.table.definitions.column.equipmentType"),
  );
  const [dirty, setDirty] = React.useState(false);
  const equipmentTypes = useCategories("EQUIPMENT_TYPE");
  const createEquipmentType = useCreateCategory("EQUIPMENT_TYPE");
  const updateEquipmentType = useUpdateCategory("EQUIPMENT_TYPE");
  const deleteEquipmentType = useDeleteCategory("EQUIPMENT_TYPE");

  const equipmentTypeIds = uniq(
    selectQuantities
      .map(q => q.equipmentTypeId)
      .map(equipmentTypeId => ({
        id: equipmentTypeId ?? "",
        name: equipmentTypeId ?? "",
      })),
  );
  const defaultValue = getDefaultValue<ApiValue>(equipmentTypeIds, "id", equipmentType => ({
    label: equipmentType.name,
    value: equipmentType.id,
  }));

  const equipmentOptions = React.useMemo(
    () => [
      { id: emptyOption.value, name: emptyOption.label },
      ...equipmentTypes.map(c => ({ id: c, name: c })),
    ],
    [equipmentTypes],
  );

  const [defaultTypeOption, setDefaultTypeOption] = React.useState(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) return null;
    return defaultValue;
  });

  const [selectedEquipmentTypes, setSelectedEquipmentTypes] =
    React.useState<SingleValue<BaseOption>>(defaultTypeOption);

  const handleEquipmentTypeChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedEquipmentTypes(option);
  }, []);

  React.useEffect(() => {
    if (selectedEquipmentTypes?.value === defaultTypeOption?.value) {
      setDirty(false);
    } else {
      setDirty(true);
    }
  }, [selectedEquipmentTypes, defaultTypeOption]);

  const resetValue = React.useCallback(() => {
    setSelectedEquipmentTypes(defaultTypeOption);
    setDirty(false);
  }, [defaultTypeOption]);

  const handleEquipmentTypeCreated = React.useCallback(
    async (newEquipmentType: string) => {
      await createEquipmentType(newEquipmentType);
      setDirty(true);

      setSelectedEquipmentTypes({
        label: newEquipmentType,
        value: newEquipmentType,
      });
    },
    [createEquipmentType],
  );

  const handleEquipmentTypeEdited = React.useCallback(
    async (option: BaseOption, label: string) => {
      if (!option.value) return;
      await updateEquipmentType(option.value, label);
      if (selectedEquipmentTypes?.value === option.value) {
        setSelectedEquipmentTypes({ ...option, label });
      }
    },
    [updateEquipmentType, setSelectedEquipmentTypes, selectedEquipmentTypes],
  );

  const handleEquipmentTypeDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (!option.value) return;
      await deleteEquipmentType(option.value);
      if (selectedEquipmentTypes?.value === option.value) {
        setDefaultTypeOption(null);
        setSelectedEquipmentTypes(null);
      }
    },
    [deleteEquipmentType, selectedEquipmentTypes],
  );

  React.useEffect(() => {
    if (defaultValue?.value === multipleValue.value)
      setPlaceholder(t("administration.modal.multiple_value"));
    else setPlaceholder(t("form.no_value"));
  }, [defaultValue]);

  return {
    placeholder,
    equipmentOptions,
    selectedEquipmentTypes,
    setSelectedEquipmentTypes,
    handleEquipmentTypeChanged,
    handleEquipmentTypeCreated,
    handleEquipmentTypeEdited,
    handleEquipmentTypeDeleted,
    dirty,
    resetValue,
  };
};
